/**
 * SHARED
 * Cross-cutting code usable by any module. Must not import from any module.
 *
 * productSpecsService :: per-tenant PRODUCT SPECIFICATIONS over RLS.
 *
 * A product_specs row is 1:1 with a products row (the SKU). Both the
 * product-specifications module and System Settings → Product SKUs ("More")
 * read/write through here, so the two screens stay in lock-step. Customers can
 * also define their own columns (product_spec_columns); those values live in
 * product_specs.custom (jsonb). Tagged photos (product_spec_images) are stored
 * in a PRIVATE bucket and read back via short-lived signed URLs.
 *
 * Writes inject the caller's account_id (so the RLS WITH CHECK passes) and only
 * send whitelisted columns (the SPEC_FIELDS keys + a sanitized custom object);
 * ids / account_id / timestamps are DB-controlled. Errors are logged in detail
 * server-side and returned generic to the caller. (migration 0044)
 */

import { supabase } from "./supabaseClient.js";
import { getMyPrimaryAccount } from "./accountService.js";
import { SPEC_FIELDS, SPEC_FIELD_KEYS } from "./productSpecFields.js";

const BUCKET = "product-spec-images";
const SIGNED_URL_TTL = 3600; // 1 hour — long enough for a session of viewing/export

let _accountIdPromise = null;
export function resetProductSpecsAccount() { _accountIdPromise = null; }

async function activeAccountId() {
  if (_accountIdPromise) return _accountIdPromise;
  _accountIdPromise = (async () => {
    const acct = await getMyPrimaryAccount();
    if (!acct?.accountId) throw new Error("No workspace found for your account.");
    return acct.accountId;
  })();
  return _accountIdPromise;
}

function writeError(where, error) {
  console.error(`[productSpecsService.${where}]`, error);
  if (error?.code === "42501" || /row-level security/i.test(error?.message ?? "")) {
    return new Error("Only members of your workspace can change these settings.");
  }
  if (error?.code === "23503") return new Error("That item is still in use and can't be deleted yet.");
  if (error?.code === "23505") return new Error("That entry already exists.");
  return new Error("Could not save your changes.");
}

// ── Coercion per field type (whitelist; unknown keys dropped) ─────────────────
const numOrNull = (v) => (v === "" || v == null ? null : (Number.isFinite(Number(v)) ? Number(v) : null));
const intOrNull = (v) => (v === "" || v == null ? null : (Number.isInteger(Number(v)) ? Number(v) : parseInt(v, 10) || null));
const textOrNull = (v) => (v == null || String(v).trim() === "" ? null : String(v).trim().slice(0, 400));

function coerceField(type, value) {
  if (type === "number") return numOrNull(value);
  if (type === "int") return intOrNull(value);
  return textOrNull(value);
}

// Whitelist the fixed spec fields out of a caller-supplied object.
function pickStandard(fields) {
  const out = {};
  for (const f of SPEC_FIELDS) {
    if (fields[f.key] === undefined) continue;
    out[f.key] = coerceField(f.type, fields[f.key]);
  }
  return out;
}

// Sanitize the free-form custom map: string keys, primitive values, capped.
function sanitizeCustom(custom) {
  if (!custom || typeof custom !== "object" || Array.isArray(custom)) return {};
  const out = {};
  let n = 0;
  for (const [k, v] of Object.entries(custom)) {
    if (n >= 100) break;
    const key = String(k).trim().slice(0, 80);
    if (!key) continue;
    if (v == null || v === "") { out[key] = null; }
    else if (typeof v === "number") { out[key] = Number.isFinite(v) ? v : null; }
    else { out[key] = String(v).slice(0, 400); }
    n++;
  }
  return out;
}

// =====================================================================
// Specs (1:1 with products)
// =====================================================================

/** All product_specs rows for the account, keyed for easy merge by product_id. */
export async function listProductSpecs() {
  const acct = await activeAccountId();
  const { data, error } = await supabase
    .from("product_specs")
    .select("*")
    .eq("account_id", acct);
  if (error) { console.error("[productSpecsService.listProductSpecs]", error); throw new Error("Could not load product specifications."); }
  return data ?? [];
}

export async function getProductSpec(productId) {
  const acct = await activeAccountId();
  const { data, error } = await supabase
    .from("product_specs")
    .select("*")
    .eq("account_id", acct)
    .eq("product_id", productId)
    .maybeSingle();
  if (error) { console.error("[productSpecsService.getProductSpec]", error); throw new Error("Could not load this product's specifications."); }
  return data ?? null;
}

/**
 * Create or update the spec for a product. Merges `custom` over the existing
 * custom map so partial saves don't wipe other custom columns.
 */
export async function upsertProductSpec(productId, fields = {}) {
  if (!productId) throw new Error("A product is required.");
  const acct = await activeAccountId();
  const existing = await getProductSpec(productId);

  const row = {
    account_id: acct,
    product_id: productId,
    ...pickStandard(fields),
  };
  if (fields.custom !== undefined) {
    row.custom = { ...(existing?.custom || {}), ...sanitizeCustom(fields.custom) };
  }

  if (existing) {
    const { error } = await supabase
      .from("product_specs")
      .update(row)
      .eq("account_id", acct)
      .eq("product_id", productId);
    if (error) throw writeError("upsertProductSpec", error);
    return { product_id: productId };
  }
  const { data, error } = await supabase
    .from("product_specs")
    .insert({ custom: {}, ...row })
    .select("id")
    .single();
  if (error) throw writeError("upsertProductSpec", error);
  return data;
}

// =====================================================================
// Custom columns (account-level definitions)
// =====================================================================

// "Distributor Item #" -> "distributorItem"
function keyFromLabel(label) {
  const words = String(label || "").trim().split(/\s+/);
  const k = words
    .map((w, i) => (i === 0 ? w.charAt(0).toLowerCase() + w.slice(1) : w.charAt(0).toUpperCase() + w.slice(1)))
    .join("")
    .replace(/[^A-Za-z0-9]/g, "");
  return k || `col${words.join("").length}`;
}

export async function listSpecColumns() {
  const acct = await activeAccountId();
  const { data, error } = await supabase
    .from("product_spec_columns")
    .select("id, key, label, data_type, sort_order")
    .eq("account_id", acct)
    .order("sort_order", { ascending: true })
    .order("label", { ascending: true });
  if (error) { console.error("[productSpecsService.listSpecColumns]", error); throw new Error("Could not load custom columns."); }
  return data ?? [];
}

export async function createSpecColumn({ label, dataType = "text", sortOrder } = {}) {
  const acct = await activeAccountId();
  const name = String(label ?? "").trim();
  if (!name) throw new Error("Column name is required.");
  if (name.length > 80) throw new Error("Column name must be 80 characters or fewer.");
  const type = dataType === "number" ? "number" : "text";
  const { data, error } = await supabase
    .from("product_spec_columns")
    .insert({
      account_id: acct,
      key: keyFromLabel(name),
      label: name,
      data_type: type,
      sort_order: Number.isFinite(Number(sortOrder)) ? Number(sortOrder) : 0,
    })
    .select("id, key, label, data_type, sort_order")
    .single();
  if (error) throw writeError("createSpecColumn", error);
  return data;
}

export async function updateSpecColumn(id, { label, dataType, sortOrder } = {}) {
  const acct = await activeAccountId();
  const patch = {};
  if (label !== undefined) { const n = String(label).trim(); if (!n) throw new Error("Column name is required."); patch.label = n.slice(0, 80); }
  if (dataType !== undefined) patch.data_type = dataType === "number" ? "number" : "text";
  if (sortOrder !== undefined) patch.sort_order = Number.isFinite(Number(sortOrder)) ? Number(sortOrder) : 0;
  const { error } = await supabase.from("product_spec_columns").update(patch).eq("account_id", acct).eq("id", id);
  if (error) throw writeError("updateSpecColumn", error);
  return { id };
}

export async function deleteSpecColumn(id) {
  const acct = await activeAccountId();
  const { error } = await supabase.from("product_spec_columns").delete().eq("account_id", acct).eq("id", id);
  if (error) throw writeError("deleteSpecColumn", error);
  return { id };
}

// =====================================================================
// Tagged images (private bucket; read via signed URLs)
// =====================================================================

async function signRows(rows) {
  if (!rows.length) return [];
  const paths = rows.map((r) => r.storage_path);
  const { data, error } = await supabase.storage.from(BUCKET).createSignedUrls(paths, SIGNED_URL_TTL);
  if (error) { console.error("[productSpecsService.signRows]", error); return rows.map((r) => ({ ...r, signedUrl: null })); }
  const byPath = new Map((data ?? []).map((d) => [d.path, d.signedUrl]));
  return rows.map((r) => ({ ...r, signedUrl: byPath.get(r.storage_path) || null }));
}

/**
 * All UPC photos for the account, grouped for the grid/export:
 *   { [product_id]: { 'product-upc': signedUrl, 'shipping-case-upc': signedUrl } }
 * One query + one batch sign for the whole sheet (the export embeds these).
 */
export async function listUpcImagesByProduct() {
  const acct = await activeAccountId();
  const { data, error } = await supabase
    .from("product_spec_images")
    .select("product_id, tag, storage_path")
    .eq("account_id", acct)
    .in("tag", ["product-upc", "shipping-case-upc"]);
  if (error) { console.error("[productSpecsService.listUpcImagesByProduct]", error); throw new Error("Could not load UPC photos."); }
  const signed = await signRows(data ?? []);
  const out = {};
  for (const r of signed) {
    if (!r.signedUrl) continue;
    (out[r.product_id] ||= {})[r.tag] = r.signedUrl;
  }
  return out;
}

export async function listProductImages(productId) {
  const acct = await activeAccountId();
  const { data, error } = await supabase
    .from("product_spec_images")
    .select("id, product_id, tag, url, storage_path, sort_order, created_at")
    .eq("account_id", acct)
    .eq("product_id", productId)
    .order("sort_order", { ascending: true })
    .order("created_at", { ascending: true });
  if (error) { console.error("[productSpecsService.listProductImages]", error); throw new Error("Could not load product images."); }
  return signRows(data ?? []);
}

/** Upload one tagged photo. Validates type client-side; the bucket also enforces
 *  an image-only MIME allowlist + size cap (no SVG). */
export async function uploadProductImage(productId, tag, file) {
  if (!productId) throw new Error("A product is required.");
  if (!file) throw new Error("No file selected.");
  const type = file.type || "";
  if (!type.startsWith("image/") || type === "image/svg+xml") {
    throw new Error("Please choose an image file (JPEG, PNG, WebP or GIF).");
  }
  const acct = await activeAccountId();
  const safeTag = String(tag || "other").trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 24) || "other";
  const ext = (file.name?.split(".").pop() || "png").toLowerCase().replace(/[^a-z0-9]/g, "") || "png";
  const rand = (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `${safeTag}-img`;
  const path = `${acct}/${productId}/${safeTag}-${rand}.${ext}`;

  const { error: upErr } = await supabase.storage.from(BUCKET).upload(path, file, { upsert: false, contentType: type });
  if (upErr) { console.error("[productSpecsService.uploadProductImage]", upErr); throw new Error("Could not upload the image."); }

  const { data, error } = await supabase
    .from("product_spec_images")
    .insert({ account_id: acct, product_id: productId, tag: safeTag, url: path, storage_path: path })
    .select("id, product_id, tag, url, storage_path, sort_order, created_at")
    .single();
  if (error) {
    // Roll back the orphaned object on a metadata-insert failure.
    await supabase.storage.from(BUCKET).remove([path]).catch(() => {});
    throw writeError("uploadProductImage", error);
  }
  const [signed] = await signRows([data]);
  return signed;
}

export async function updateProductImageTag(id, tag) {
  const acct = await activeAccountId();
  const safeTag = String(tag || "other").trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 24) || "other";
  const { error } = await supabase.from("product_spec_images").update({ tag: safeTag }).eq("account_id", acct).eq("id", id);
  if (error) throw writeError("updateProductImageTag", error);
  return { id, tag: safeTag };
}

export async function deleteProductImage(id) {
  const acct = await activeAccountId();
  const { data: row } = await supabase
    .from("product_spec_images").select("storage_path").eq("account_id", acct).eq("id", id).maybeSingle();
  const { error } = await supabase.from("product_spec_images").delete().eq("account_id", acct).eq("id", id);
  if (error) throw writeError("deleteProductImage", error);
  if (row?.storage_path) await supabase.storage.from(BUCKET).remove([row.storage_path]).catch(() => {});
  return { id };
}

// Re-export the field metadata so module code has one import for everything.
export { SPEC_FIELDS, SPEC_FIELD_KEYS } from "./productSpecFields.js";
